import { LegalPage, LegalSection } from "@/components/LegalPage";

export default function Terms() {
  return (
    <LegalPage
      testId="terms-page"
      title="Terms & Conditions"
      subtitle="These terms govern your access to and use of the MovaRaBoard shipment-visibility platform. Please read them carefully before using the service."
      updated="July 7, 2026"
    >
      <p>
        These Terms &amp; Conditions ("Terms") are a binding agreement between MovaRaBoard ("MovaRaBoard",
        "we", "us", or "our") and the business entity ("Customer", "you", or "your") that registers for,
        accesses, or uses the MovaRaBoard platform, dashboards, APIs, and related services (collectively, the
        "Service"). By creating a workspace, accepting a workspace invitation, or otherwise using the Service,
        you agree to be bound by these Terms.
      </p>

      <LegalSection heading="1. The Service">
        <p>
          MovaRaBoard provides a multi-tenant, cloud-based shipment-visibility and dispatch-coordination
          platform for logistics teams. Features include shipment tracking boards, document management,
          delivery verification, email invitations, and public read-only tracking links. We may add, modify,
          or discontinue features from time to time to improve the Service.
        </p>
      </LegalSection>

      <LegalSection heading="2. Workspaces & Accounts">
        <p>
          The Service is organized into workspaces scoped to a single company. Each workspace administrator is
          responsible for managing users, roles, and permissions within their workspace, and for ensuring that
          all authorized users comply with these Terms. You are responsible for maintaining the confidentiality
          of your account credentials and for all activity that occurs under your account.
        </p>
      </LegalSection>

      <LegalSection heading="3. Customer Data & Tenant Isolation">
        <p>
          You retain all rights, title, and interest in the data you submit to the Service ("Customer Data").
          MovaRaBoard enforces strict tenant isolation: Customer Data is scoped to your workspace and is not
          accessible to other tenants. We process Customer Data solely to provide and support the Service, in
          accordance with our Privacy Policy.
        </p>
        <p>
          Public tracking links (e.g., <span className="font-mono-data text-ink">/track/&#123;token&#125;</span>) expose
          only the limited, delivery-relevant fields of a single shipment and never expose tenant lists,
          financial data, or the ability to modify records.
        </p>
      </LegalSection>

      <LegalSection heading="4. Acceptable Use">
        <p>You agree not to, and not to permit any user to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>use the Service in violation of any applicable law or regulation;</li>
          <li>attempt to access data belonging to another tenant or circumvent tenant isolation;</li>
          <li>reverse engineer, resell, or provide unauthorized third-party access to the Service;</li>
          <li>upload malicious code or content that infringes the rights of others;</li>
          <li>interfere with or disrupt the integrity or performance of the Service.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="5. Subscriptions, Fees & Billing">
        <p>
          Paid plans are billed on a recurring subscription basis through our payment processor. By selecting a
          plan and completing checkout, you authorize recurring charges for the applicable subscription period.
          Fees are non-refundable except where required by law. We may change plan pricing with prior notice
          effective at your next billing cycle.
        </p>
      </LegalSection>

      <LegalSection heading="6. Service Availability">
        <p>
          We strive to keep the Service available and reliable, but we do not guarantee uninterrupted or
          error-free operation. The Service is provided on an "as available" basis and may be subject to
          scheduled maintenance or factors beyond our reasonable control.
        </p>
      </LegalSection>

      <LegalSection heading="7. Third-Party Services">
        <p>
          The Service integrates with third-party providers (for example, email delivery, object storage, and
          payment processing). Your use of those integrations may be subject to the applicable third party's
          terms. We are not responsible for the acts or omissions of third-party providers.
        </p>
      </LegalSection>

      <LegalSection heading="8. Confidentiality">
        <p>
          Each party may access confidential information of the other. Both parties agree to protect such
          information with reasonable care and to use it only as necessary to perform under these Terms.
        </p>
      </LegalSection>

      <LegalSection heading="9. Warranties & Disclaimers">
        <p>
          Except as expressly stated, the Service is provided "as is" without warranties of any kind, whether
          express, implied, or statutory, including any implied warranties of merchantability, fitness for a
          particular purpose, and non-infringement.
        </p>
      </LegalSection>

      <LegalSection heading="10. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, neither party will be liable for any indirect, incidental,
          special, consequential, or punitive damages. MovaRaBoard's aggregate liability arising out of or
          related to these Terms will not exceed the fees paid by you in the twelve (12) months preceding the
          event giving rise to the claim.
        </p>
      </LegalSection>

      <LegalSection heading="11. Term & Termination">
        <p>
          These Terms remain in effect while you use the Service. Either party may terminate for material
          breach that remains uncured after reasonable notice. Upon termination, your right to access the
          Service ceases, and we will make Customer Data available for export for a limited period before
          deletion in accordance with our data-retention practices.
        </p>
      </LegalSection>

      <LegalSection heading="12. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will be communicated through the
          Service or by email. Your continued use of the Service after changes take effect constitutes
          acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection heading="13. Contact">
        <p>
          Questions about these Terms can be directed to{" "}
          <a href="mailto:support@movaraboard.com" className="font-semibold text-[#0B132B] underline">
            support@movaraboard.com
          </a>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
